import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Grid, Box, Typography, Card, Avatar, CircularProgress, Alert } from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Rule as RuleIcon,
    Payments as PaymentsIcon,
    SyncProblem as SyncProblemIcon,
    MoreHoriz as MoreHorizIcon
} from "@mui/icons-material";

import { DataLayout, StatusAvatar, ProjectTimeline, PerformanceChart, StatusChip, BudgetCard } from "../../../shared/components";
import { formatCurrency } from "../../../shared/utils";
import { clientTheme } from "../../../theme/clientTheme";

import { useStageGate } from "../hooks/useStageGate";
import { useRagStatus } from "../hooks/useRagStatus";
import { usePhase } from "../hooks/usePhase";
import { useCostTrend } from "../hooks/useCostTrend";
import { useOverviewStats } from "../hooks/useOverviewStats";
import { useFetchCriticalActions } from "../hooks/useFetchCriticalActions";
import { useFetchProjectActivityList } from "../hooks/useFetchProjectActivityList";
import { MetaDrivenTable } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

export const Dashboard: React.FC = () => {

    const { projectId } = useParams<{ projectId: string }>();

    const { isPending: isStageGateDetailsPending, stageGateData } = useStageGate(projectId);
    const { isPending: isRagStatusDetailsPending, ragStatusData } = useRagStatus(projectId);
    const { isPending: isOverviewStatsDetailsPending, overviewStatsData } = useOverviewStats(projectId);
    const { isPending: isCostTrendDetailsPending, costTrendData } = useCostTrend(projectId);
    const { isPending: isCriticalActionsPending, criticalActionsData } = useFetchCriticalActions(projectId);
    const { data: activityListData, isLoading: isActivityListLoading } = useFetchProjectActivityList(
        clientTheme.layout?.showProjectActivityList ? projectId : undefined
    );

    const phaseData = usePhase();
    let isPhasesDetailsPending = phaseData.isPending;

    useEffect(() => {
        phaseData.mutateAsync(projectId);
    }, [projectId]);

    if (isPhasesDetailsPending || isRagStatusDetailsPending || isStageGateDetailsPending || isOverviewStatsDetailsPending || isCostTrendDetailsPending || isCriticalActionsPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Grid container spacing={2} p={2}>

            <Grid size={12}>
                <DataLayout title="Project Timeline">
                    <ProjectTimeline steps={phaseData?.data?.data || []} />
                </DataLayout>
            </Grid>

            <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                    {/* Stage Gate Status */}
                    <DataLayout title="Stage Gate Status">
                        <Box
                            display="flex"
                            gap={2}
                            flexDirection="column"
                            p={2}
                            sx={{
                                height: 250,
                                overflowY: 'auto'
                            }}
                        >
                            {stageGateData.length === 0 ? (
                                <Box my="auto">
                                    <Alert severity="warning">Stage Gate to be Defined</Alert>
                                </Box>
                            ) : (
                                stageGateData.map(
                                    (
                                        gate: {
                                            name: string;
                                            status: string;
                                            status_display_text: string;
                                            color: 'blue' | 'red' | 'amber';
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                            flex={1}
                                            key={index}
                                        >
                                            <StatusAvatar
                                                bgcolor={
                                                    gate.color === 'blue'
                                                        ? 'info.main'
                                                        : gate.color === 'amber'
                                                            ? 'warning.main'
                                                            : gate.color === 'red'
                                                                ? 'error.main'
                                                                : 'default'
                                                }
                                            >
                                                {gate.color === 'blue' ? (
                                                    <CheckCircleIcon />
                                                ) : gate.color === 'amber' ? (
                                                    <MoreHorizIcon />
                                                ) : gate.color === 'red' ? (
                                                    <SyncProblemIcon />
                                                ) : (
                                                    <SyncProblemIcon />
                                                )}
                                            </StatusAvatar>
                                            <Typography variant="subtitle2" color="text.primary">
                                                {gate.name}
                                            </Typography>
                                            <StatusChip
                                                sx={{ ml: 'auto' }}
                                                label={gate.status_display_text}
                                                size="small"
                                                color={gate.color as 'red' | 'blue' | 'amber'}
                                                variant="outlined"
                                            />
                                        </Box>
                                    )
                                )
                            )}
                        </Box>
                    </DataLayout>

                    {/* Critical Actions */}
                    <DataLayout title="Critical Actions">
                        <Box
                            mx={1}
                            my={2}
                            display="flex"
                            flexDirection="column"
                            justifyContent="start"
                            gap={2}
                            px={1}
                        >
                            {
                                criticalActionsData.length === 0 ? (
                                    <Box my="auto">
                                        <Alert severity="success">No Critical Actions</Alert>
                                    </Box>
                                ) : (
                                    criticalActionsData.map(
                                        (
                                            action: {
                                                details: string;
                                                due_date: string;
                                                severity : "CRITICAL" | "HIGH" | "AVERAGE"
                                                severity_display_text: 'Critical' | 'High Risk' | 'Average'
                                            },
                                            index: number
                                        ) => (
                                            <Box
                                                key={index}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={1}
                                            >
                                                <Box display="flex" flexDirection="column">
                                                    <Typography variant="subtitle2" color="text.primary">
                                                        {action.details || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {action.due_date ? `Due on ${formatDate(action.due_date)}` : 'No due date'}
                                                    </Typography>
                                                </Box>
                                                <StatusChip label={action.severity_display_text} color={action.severity === 'CRITICAL' ? 'red' : action.severity === 'HIGH' ? 'amber' : 'green'} variant="filled" />
                                            </Box>
                                        )
                                    )
                                )
                            }
                        </Box>
                    </DataLayout>

                    {/* RAG Status */}
                    <DataLayout title="RAG Status">
                        <Box
                            mx={1}
                            my={2}
                            display="flex"
                            flexDirection="column"
                            justifyContent="start"
                            gap={2}
                            px={1}
                        >
                            {['cost', 'time', 'quality'].map((value, index) => (
                                <Card
                                    key={index}
                                    variant="outlined"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderColor:
                                            ragStatusData[value] === 'green'
                                                ? 'success.main'
                                                : ragStatusData[value] === 'amber'
                                                    ? 'warning.main'
                                                    : 'error.main',
                                        gap: 2,
                                        px: 1,
                                        py: 1,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            backgroundColor:
                                                ragStatusData[value] === 'green'
                                                    ? 'success.main'
                                                    : ragStatusData[value] === 'amber'
                                                        ? 'warning.main'
                                                        : 'error.main',
                                            borderRadius: 1,
                                        }}
                                    >
                                        {value === 'cost' ? (
                                            <PaymentsIcon />
                                        ) : value === 'time' ? (
                                            <RuleIcon />
                                        ) : (
                                            <AccessTimeIcon />
                                        )}
                                    </Avatar>
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="subtitle2">
                                            {value?.charAt(0).toUpperCase() + value?.slice(1)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={
                                                ragStatusData[value] === 'green'
                                                    ? 'success.main'
                                                    : ragStatusData[value] === 'amber'
                                                        ? 'warning.main'
                                                        : 'error.main'
                                            }
                                        >
                                            {ragStatusData[value]?.charAt(0).toUpperCase() + ragStatusData[value]?.slice(1)}
                                        </Typography>
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    </DataLayout>
                </Box>
            </Grid>

            {!clientTheme.layout?.hideProjectCommercialDashboard && (
            <Grid size={12}>
                <Grid container spacing={2}>
                    {(() => {
                        const totalBudget = overviewStatsData?.total_budget || 0;
                        const spentToDate = overviewStatsData?.spent_to_date || 0;
                        const remainingBudget = totalBudget - spentToDate;
                        const forecastVariance = overviewStatsData?.forecast_variance || 0;
                        return [
                            { label: 'Total Budget', value: formatCurrency(totalBudget).replace('£', '').trim(), icon: 'Balance', color: 'green' as const },
                            { label: 'Spent to Date', value: formatCurrency(spentToDate).replace('£', '').trim(), icon: 'Cost', color: 'amber' as const },
                            { label: 'Remaining Budget', value: formatCurrency(remainingBudget).replace('£', '').trim(), icon: 'Money', color: 'green' as const },
                            { label: 'Forecast Variance', value: `${formatCurrency(forecastVariance).replace('£', '').trim()}`, icon: 'Trending', color: 'red' as const },
                        ].map((value, index) => (
                            <Grid size={3} key={index}>
                                <BudgetCard label={value.label} value={value.value} icon={value.icon} color={value.color} />
                            </Grid>
                        ));
                    })()}
                </Grid>
            </Grid>
            )}

            {!clientTheme.layout?.hideProjectCommercialDashboard && (
            <Grid size={12}>
                <DataLayout title="Cost Trend Analysis">
                    <PerformanceChart data={costTrendData} />
                </DataLayout>
            </Grid>
            )}

            {clientTheme.layout?.showProjectActivityList && (
            <Grid size={12}>
                <DataLayout title="Activity List">
                    {isActivityListLoading ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <MetaDrivenTable
                            headers={[
                                { alias_name: "rag", label: "RAG", display_order: 1, is_show: true },
                                { alias_name: "activity_id", label: "ID", display_order: 2, is_show: true },
                                { alias_name: "activity_name", label: "Activity Name", display_order: 3, is_show: true },
                                { alias_name: "status_display_text", label: "Status", display_order: 4, is_show: true },
                                { alias_name: "phase_name", label: "Phase", display_order: 5, is_show: true },
                                { alias_name: "region_description", label: "Region", display_order: 6, is_show: true },
                                { alias_name: "days_in_phase", label: "Days in Phase", display_order: 7, is_show: true },
                                { alias_name: "query_count", label: "Queries", display_order: 8, is_show: true },
                                { alias_name: "materials", label: "Materials", display_order: 9, is_show: true },
                            ]}
                            data={activityListData}
                            dataMetrics={{
                                rag: {
                                    type: "component",
                                    element: (value: { rag: string }) => (
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                bgcolor:
                                                    value.rag === "amber"
                                                        ? "warning.main"
                                                        : value.rag === "green"
                                                            ? "success.main"
                                                            : "error.main",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    ),
                                },
                                status_display_text: {
                                    type: "component",
                                    element: (value: { status_display_text: string }) => (
                                        <StatusChip
                                            label={value.status_display_text}
                                            color={
                                                value.status_display_text === "In Progress"
                                                    ? "amber"
                                                    : value.status_display_text === "Completed" || value.status_display_text === "Complete"
                                                        ? "green"
                                                        : "red"
                                            }
                                            variant="filled"
                                        />
                                    ),
                                },
                                query_count: {
                                    type: "component",
                                    element: (value: { query_count: number }) => (
                                        <StatusChip
                                            label={String(value.query_count)}
                                            color="blue"
                                            variant="filled"
                                        />
                                    ),
                                },
                                materials: {
                                    type: "component",
                                    element: (value: { materials: string }) => (
                                        <StatusChip
                                            label={String(value.materials) || "N/A"}
                                            color={
                                                value.materials === "Confirmed"
                                                    ? "green"
                                                    : value.materials === "Pending"
                                                        ? "amber"
                                                        : "red"
                                            }
                                            variant="filled"
                                        />
                                    ),
                                },
                            }}
                            sortDisabled
                        />
                    )}
                </DataLayout>
            </Grid>
            )}

        </Grid>
    );
};  